import { parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { AutoGrowTextarea } from '@/components/ui/autoGrowTextarea';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useSessionSummaryQuery } from '@/hooks/useSessionSummaryQuery';
import {
  useApproveSessionSummaryMutation,
  useSaveSessionSummaryMutation,
  useShareSessionSummaryMutation,
  useUnshareSessionSummaryMutation,
} from '@/hooks/useSessionSummaryMutations';
import { useDownloadSummaryPdf, useViewSummaryPdf } from '@/hooks/useDownloadSummaryPdf';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface SummaryCardProps {
  noteId: string | null;
}

function toTextValue(value: string | null): string {
  return value === null ? '' : value;
}

function toNullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function SummaryCard({ noteId }: SummaryCardProps) {
  const { t } = useTranslation('patients');
  const summaryQuery = useSessionSummaryQuery(noteId === null ? '' : noteId);
  const saveMutation = useSaveSessionSummaryMutation();
  const approveMutation = useApproveSessionSummaryMutation();
  const shareMutation = useShareSessionSummaryMutation();
  const unshareMutation = useUnshareSessionSummaryMutation();
  const downloadPdfMutation = useDownloadSummaryPdf();
  const viewPdfMutation = useViewSummaryPdf();

  const [isInitialized, setIsInitialized] = useState(false);
  const [plainLanguageText, setPlainLanguageText] = useState('');
  const [medicalLanguageText, setMedicalLanguageText] = useState('');
  const [sharedWithClientAt, setSharedWithClientAt] = useState<string | null>(null);
  const [isConfirmingSharedEdit, setIsConfirmingSharedEdit] = useState(false);
  const [isStopSharingConfirmOpen, setIsStopSharingConfirmOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = useState('');

  useEffect(
    function loadSummary() {
      if (isInitialized || summaryQuery.data === undefined) {
        return;
      }
      setPlainLanguageText(toTextValue(summaryQuery.data.plainLanguageText));
      setMedicalLanguageText(toTextValue(summaryQuery.data.medicalLanguageText));
      setSharedWithClientAt(summaryQuery.data.sharedWithClientAt);
      setIsInitialized(true);
    },
    [isInitialized, summaryQuery.data]
  );

  useEffect(
    function revokePreviewUrlOnUnmount() {
      return function cleanup() {
        if (previewUrl !== null) {
          URL.revokeObjectURL(previewUrl);
        }
      };
    },
    [previewUrl]
  );

  if (noteId === null) {
    return (
      <Card className="space-y-2">
        <p className="text-sm font-bold text-zooindigo">
          {t('patients:sessionNote.summary.title')}
        </p>
        <p className="text-[11px] text-zoomuted">{t('patients:sessionNote.summary.savePrompt')}</p>
      </Card>
    );
  }

  const resolvedNoteId = noteId;
  const isBusy =
    saveMutation.isPending ||
    approveMutation.isPending ||
    shareMutation.isPending ||
    unshareMutation.isPending;
  const isShared = sharedWithClientAt !== null;
  const hasAnyText = plainLanguageText.trim().length > 0 || medicalLanguageText.trim().length > 0;
  const statusTone: BadgeTone = isShared ? 'success' : 'neutral';
  const statusLabel = isShared
    ? t('patients:sessionNote.summary.statusSharedOn', {
        date:
          sharedWithClientAt === null ? '' : formatDateToDayMonthYear(parseISO(sharedWithClientAt)),
      })
    : t('patients:sessionNote.summary.statusNotShared');

  async function save() {
    try {
      const saved = await saveMutation.mutateAsync({
        noteId: resolvedNoteId,
        payload: {
          plainLanguageText: toNullableText(plainLanguageText),
          medicalLanguageText: toNullableText(medicalLanguageText),
        },
      });
      setSharedWithClientAt(saved.sharedWithClientAt);
      showSuccessNotification(t('patients:sessionNote.summary.saved'));
    } catch {
      showErrorNotification(t('patients:sessionNote.summary.saveError'));
    }
  }

  async function shareWithOwner() {
    try {
      const saved = await saveMutation.mutateAsync({
        noteId: resolvedNoteId,
        payload: {
          plainLanguageText: toNullableText(plainLanguageText),
          medicalLanguageText: toNullableText(medicalLanguageText),
        },
      });
      setSharedWithClientAt(saved.sharedWithClientAt);
      const approved = await approveMutation.mutateAsync(resolvedNoteId);
      setSharedWithClientAt(approved.sharedWithClientAt);
      const shared = await shareMutation.mutateAsync(resolvedNoteId);
      setSharedWithClientAt(shared.sharedWithClientAt);
      showSuccessNotification(t('patients:sessionNote.summary.shared'));
    } catch {
      showErrorNotification(t('patients:sessionNote.summary.shareError'));
    }
  }

  async function stopSharing() {
    try {
      const unshared = await unshareMutation.mutateAsync(resolvedNoteId);
      setSharedWithClientAt(unshared.sharedWithClientAt);
      setIsStopSharingConfirmOpen(false);
      showSuccessNotification(t('patients:sessionNote.summary.unshared'));
    } catch {
      showErrorNotification(t('patients:sessionNote.summary.shareError'));
    }
  }

  async function viewPdf() {
    try {
      const downloaded = await viewPdfMutation.mutateAsync(resolvedNoteId);
      const objectUrl = URL.createObjectURL(downloaded.blob);
      setPreviewFilename(downloaded.filename);
      setPreviewUrl(objectUrl);
    } catch {
      showErrorNotification(t('patients:sessionNote.summary.viewPdfError'));
    }
  }

  function closePreview() {
    if (previewUrl !== null) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
            <Icon name="Send" />
          </span>
          <p className="text-sm font-bold text-zooindigo">
            {t('patients:sessionNote.summary.title')}
          </p>
        </div>
        <Badge tone={statusTone} size="md">
          {statusLabel}
        </Badge>
      </div>

      <label className="block text-xs font-semibold text-zoomuted">
        {t('patients:sessionNote.summary.plainLanguageLabel')}
        <span className="block text-[11px] font-normal text-zoomuted mt-0.5">
          {t('patients:sessionNote.summary.plainLanguageHint')}
        </span>
        <AutoGrowTextarea
          value={plainLanguageText}
          disabled={isBusy}
          rows={3}
          placeholder={t('patients:sessionNote.summary.plainLanguagePlaceholder')}
          onChange={function changePlainLanguage(event) {
            setPlainLanguageText(event.target.value);
          }}
          className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
        />
      </label>

      <label className="block text-xs font-semibold text-zoomuted">
        {t('patients:sessionNote.summary.medicalLanguageLabel')}
        <span className="block text-[11px] font-normal text-zoomuted mt-0.5">
          {t('patients:sessionNote.summary.medicalLanguageHint')}
        </span>
        <AutoGrowTextarea
          value={medicalLanguageText}
          disabled={isBusy}
          rows={3}
          placeholder={t('patients:sessionNote.summary.medicalLanguagePlaceholder')}
          onChange={function changeMedicalLanguage(event) {
            setMedicalLanguageText(event.target.value);
          }}
          className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
        />
      </label>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="small"
            onClick={function onSave() {
              if (isShared) {
                setIsConfirmingSharedEdit(true);
                return;
              }
              void save();
            }}
            disabled={isBusy}
            isLoading={saveMutation.isPending}
          >
            {t('patients:sessionNote.summary.save')}
          </Button>
          <Button
            variant="outline"
            size="small"
            onClick={function onViewPdf() {
              void viewPdf();
            }}
            disabled={isBusy || hasAnyText === false}
            isLoading={viewPdfMutation.isPending}
          >
            <Icon name="Eye" />
            {t('patients:sessionNote.summary.viewPdf')}
          </Button>
          <Button
            variant="outline"
            size="small"
            onClick={function onDownloadPdf() {
              downloadPdfMutation.mutate(resolvedNoteId, {
                onError: function notifyDownloadFailure() {
                  showErrorNotification(t('patients:sessionNote.summary.downloadError'));
                },
              });
            }}
            disabled={isBusy || hasAnyText === false}
            isLoading={downloadPdfMutation.isPending}
          >
            <Icon name="Download" />
            {t('patients:sessionNote.summary.downloadPdf')}
          </Button>
        </div>
        {isShared ? (
          <Button
            variant="outline"
            size="small"
            onClick={function openStopSharingConfirm() {
              setIsStopSharingConfirmOpen(true);
            }}
            disabled={isBusy}
          >
            {t('patients:sessionNote.summary.stopSharing')}
          </Button>
        ) : (
          <Button
            size="small"
            onClick={function onShareWithOwner() {
              void shareWithOwner();
            }}
            disabled={isBusy || hasAnyText === false}
            isLoading={isBusy}
          >
            <Icon name="Send" />
            {t('patients:sessionNote.summary.shareWithOwner')}
          </Button>
        )}
      </div>

      <FilePreviewModal
        isOpen={previewUrl !== null}
        onClose={closePreview}
        url={previewUrl === null ? '' : previewUrl}
        filename={previewFilename}
        kind="pdf"
      />

      {isStopSharingConfirmOpen ? (
        <ConfirmDialog
          title={t('patients:sessionNote.summary.stopSharingConfirmTitle')}
          description={t('patients:sessionNote.summary.stopSharingConfirmDescription')}
          confirmLabel={t('patients:sessionNote.summary.stopSharing')}
          iconName="Eye"
          isConfirming={unshareMutation.isPending}
          onConfirm={function confirmStopSharing() {
            void stopSharing();
          }}
          onCancel={function cancelStopSharing() {
            setIsStopSharingConfirmOpen(false);
          }}
        />
      ) : null}
      {isConfirmingSharedEdit ? (
        <ConfirmDialog
          title={t('patients:sessionNote.summary.sharedEditConfirmTitle')}
          description={t('patients:sessionNote.summary.sharedEditConfirmDescription')}
          confirmLabel={t('patients:sessionNote.summary.sharedEditConfirm')}
          isConfirming={saveMutation.isPending}
          onConfirm={function confirmSharedEdit() {
            setIsConfirmingSharedEdit(false);
            void save();
          }}
          onCancel={function cancelSharedEdit() {
            setIsConfirmingSharedEdit(false);
          }}
        />
      ) : null}
    </Card>
  );
}
